import { API_BASE_URL } from "../../../config";
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import CustomerLink from '../../Components/CustomerLink';
import {
    aggregatePayments, mergeDenoms, buildProductRows, buildSummary, BAG_DEPOSIT_NAME, buildWarnings, groupByRoute, resolveRouteCash, round2,
} from './accountingDailyCalc';

// หน้า "รายงานบัญชีรายวัน" — ตรงแผ่นของฝ่ายบัญชี 5 ตาราง (Vector 2026-10-07) · แทนหน้า /sales-report-summary เดิม (เลิกแล้ว 2026-10-11 — URL เดิม redirect มาที่นี่)
// "กระสอบ" ของบัญชี = "ถุง" ในระบบ หน่วยเดียวกัน · เงินสดย่อย/น้ำมัน/อื่น ๆ/คืนประกัน บัญชีกรอกเอง ไม่คำนวณ

// AGC-TOKYO = เอเย่นต์ของโรงงานเอง นับเป็น "สาย" (Vector 2026-07-07 เหมือนหน้ารายงานการขายรวม) · เอเย่นต์อื่นรวมเป็นแถว "เอเย่นต์"
const FACTORY_AGENT_ID = 'AGC-TOKYO';

const fmt = (n) => {
    const v = typeof n === 'number' && !isNaN(n) ? n : (parseFloat(n) || 0);
    return v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};
const fmtInt = (n) => {
    const v = typeof n === 'number' && !isNaN(n) ? n : (parseInt(n, 10) || 0);
    return v.toLocaleString('en-US');
};
const todayStr = () => {
    const d = new Date();
    const pad = (x) => String(x).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};
const thaiDate = (iso) => {
    const [y, m, d] = String(iso).split('-').map(Number);
    return y ? `${d}/${m}/${y}` : iso;
};
const authHeaders = (token) => ({ Authorization: `Bearer ${token}` });
const getJson = (url, token, fallback) =>
    fetch(url, { headers: authHeaders(token) }).then(res => (res.ok ? res.json() : fallback)).catch(() => fallback);

const PINK = '#f8cbd9';
const PINK_DARK = '#f4a7bf';
const YELLOW = '#fff200';
const border = '1px solid #444';
const cell = { border, padding: '4px 8px', fontSize: '13px' };
const th = { ...cell, background: PINK, fontWeight: 700, textAlign: 'center' };
const right = { textAlign: 'right' };
const total = { ...cell, background: PINK, fontWeight: 700 };
const tableStyle = { width: '100%', borderCollapse: 'collapse', marginBottom: '18px', background: '#fff' };
const inputStyle = { width: '110px', padding: '2px 6px', border: '1px solid #ced4da', borderRadius: '4px', textAlign: 'right', fontSize: '13px' };

const PRINT_CSS = `
@page { margin: 10mm; }
@media print {
  body * { visibility: hidden !important; }
  .acct-print-root, .acct-print-root * { visibility: visible !important; }
  .acct-print-root { position: absolute; left: 0; top: 0; width: 100%; max-width: none !important; margin: 0 !important; padding: 0 !important; box-sizing: border-box; }
  .acct-no-print { display: none !important; }
  .acct-print-root table { page-break-inside: avoid; }
  .acct-print-root input { border: none !important; background: transparent !important; padding: 0 !important; }
}`;

// ยอดคืนประกันกระสอบของวัน: บัญชีกรอกเอง (>0) ทับยอดจาก IMD หน้าโรง · ช่องว่าง/0 = ใช้ยอดที่คนหน้าโรงกดคืนจากเครื่อง
export const bagReturnEffective = (entered, imd) => {
    const typed = Number(entered) || 0;
    return typed > 0 ? typed : (imd ? Number(imd.amount) || 0 : 0);
};
export const bagReturnSourceNote = (entered, imd) => {
    const typed = Number(entered) || 0;
    if (typed > 0) return imd && imd.amount > 0 ? `บัญชีกรอกเอง (IMD หน้าโรงบันทึกคืน ${imd.qty} ใบ ฿${imd.amount})` : 'บัญชีกรอกเอง';
    if (imd && imd.amount > 0) return `จาก IMD หน้าโรง ${imd.qty} ใบ (เราคืนเงินให้ลูกค้า — เงินออก)`;
    return imd ? 'เราคืนเงินให้ลูกค้า (เงินออก)' : 'อ่านยอดจาก IMD ไม่สำเร็จ — ใช้เฉพาะที่กรอก';
};

const EMPTY_ENTRY = { otherExpenses: 0, pettyCash: 0, bagDepositReturn: 0 };

const AccountingDailyReport = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const [date, setDate] = useState(searchParams.get('date') || todayStr());

    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState('');
    const [routeReports, setRouteReports] = useState([]); // สายรถ + AGC-TOKYO
    const [agentReports, setAgentReports] = useState([]); // เอเย่นต์อื่น
    const [fullByCode, setFullByCode] = useState({});
    const [roundByCode, setRoundByCode] = useState({}); // รอบหน้าโรง: เงินส่ง + ยอดที่ต้องส่งตามระบบ (seller web endpoint)
    const [bagSales, setBagSales] = useState({ rows: [], qty: 0, total: 0 });
    const [denoms, setDenoms] = useState([]);
    const [debts, setDebts] = useState([]);
    const [forgotRows, setForgotRows] = useState([]); // รายงานลืมส่งของวัน (ย้ายมาจาก sales-report-summary)
    const [agentNameMap, setAgentNameMap] = useState({});
    // เงินประกันกระสอบที่คืนที่หน้าโรง (IMD บัตรขายหน้าโรง) — null = อ่านไม่สำเร็จ (ไม่ใช่ 0)
    const [imdBagReturn, setImdBagReturn] = useState({ qty: 0, amount: 0 });

    const [entry, setEntry] = useState(EMPTY_ENTRY);
    const [entryDirty, setEntryDirty] = useState(false);
    const [entryLoaded, setEntryLoaded] = useState(false);
    const [saving, setSaving] = useState(false);
    const [saveMsg, setSaveMsg] = useState('');

    const checkToken = useCallback(() => {
        const user = localStorage.getItem('user');
        if (!user) { navigate('/login'); return null; }
        return JSON.parse(user)?.token;
    }, [navigate]);

    const loadAll = useCallback(async (d) => {
        const token = checkToken();
        if (!token) return;
        setLoading(true);
        setLoadError('');
        setEntryLoaded(false);
        try {
            const base = `${API_BASE_URL}/saledata/api/module`;
            const [delRaw, agentRaw, denomRaw, debtRaw, entryRaw, agentNames, imdSummary, forgotRaw] = await Promise.all([
                getJson(`${base}/reports/by-date?startDate=${d}&endDate=${d}&isAgent=false`, token, null),
                getJson(`${base}/reports/by-date?startDate=${d}&endDate=${d}&isAgent=true`, token, null),
                getJson(`${base}/payment-codes?category=CASH_DENOMINATION`, token, []),
                getJson(`${base}/credit-debts`, token, null),
                getJson(`${API_BASE_URL}/saledata/api/module/accounting-daily?date=${d}`, token, null),
                fetch(`${API_BASE_URL}/customer/api/customers/by-category`, {
                    method: 'POST',
                    headers: { ...authHeaders(token), 'Content-Type': 'application/json' },
                    body: JSON.stringify({ type: 'AGENT' }),
                }).then(res => (res.ok ? res.json() : null)).catch(() => null),
                getJson(`${base}/front-counter/bag-deposit/summary?from=${d}&to=${d}`, token, null),
                getJson(`${base}/reports/forgot-sales-summary?startDate=${d}&endDate=${d}`, token, []),
            ]);
            // เรียกรายการหลักล้ม ≠ "ไม่มีข้อมูล" — ต้องบอก ไม่งั้นบัญชีเห็นเลข 0 แล้วเข้าใจว่าวันนั้นไม่มีขาย
            if (!Array.isArray(delRaw) || !Array.isArray(agentRaw) || !Array.isArray(debtRaw)) {
                setLoadError('โหลดข้อมูลบางส่วนไม่สำเร็จ — ตัวเลขด้านล่างอาจไม่ครบ ลองเปลี่ยนวันที่หรือโหลดหน้าใหม่');
            }
            const delivery = Array.isArray(delRaw) ? delRaw : [];
            const agents = Array.isArray(agentRaw) ? agentRaw : [];
            const isFactory = (r) => (r.sellerId || '').toUpperCase() === FACTORY_AGENT_ID;
            const routes = [...delivery, ...agents.filter(isFactory)];
            const others = agents.filter(r => !isFactory(r));
            const all = [...routes, ...others];

            const factoryCodes = all.filter(isFactory).map(r => r.reportCode);
            const [fulls, bagPer, factoryRounds] = await Promise.all([
                Promise.all(all.map(r => getJson(`${base}/reports/${encodeURIComponent(r.reportCode)}/full`, token, null))),
                Promise.all(all.map(r => getJson(`${base}/bag-sales/report/${encodeURIComponent(r.reportCode)}`, token, []))),
                Promise.all(factoryCodes.map(code => getJson(`${API_BASE_URL}/seller/api/tablet/report/web/rounds/${encodeURIComponent(code)}`, token, null))),
            ]);
            const roundMap = {};
            factoryCodes.forEach((code, i) => { roundMap[code] = factoryRounds[i]; });
            const fullMap = {};
            all.forEach((r, i) => { fullMap[r.reportCode] = fulls[i]; });
            if (fulls.some(f => f == null)) setLoadError('โหลดรายละเอียดบางใบรายงานไม่สำเร็จ — ตัวเลขด้านล่างอาจไม่ครบ');

            const bagMap = new Map();
            let bagQty = 0, bagTotal = 0;
            bagPer.flat().forEach(sale => {
                bagTotal += sale?.total || 0;
                (sale?.normalizedEntries || []).forEach(e => {
                    bagQty += e.quantity || 0;
                    const name = BAG_DEPOSIT_NAME;
                    const key = `${name}__${e.unitPrice || 0}`;
                    if (!bagMap.has(key)) bagMap.set(key, { name, unitPrice: e.unitPrice || 0, qty: 0, total: 0 });
                    const row = bagMap.get(key);
                    row.qty += e.quantity || 0;
                    row.total += e.total || 0;
                });
            });

            const names = {};
            const agentList = Array.isArray(agentNames) ? agentNames : (agentNames?.agent || []);
            agentList.forEach(a => { if (a.agentCustomerUid) names[a.agentCustomerUid.toUpperCase()] = a.agentName || ''; });

            setRouteReports(routes);
            setAgentReports(others);
            setFullByCode(fullMap);
            setRoundByCode(roundMap);
            setBagSales({ rows: Array.from(bagMap.values()), qty: bagQty, total: bagTotal });
            setDenoms(
                (Array.isArray(denomRaw) ? denomRaw : [])
                    .filter(r => r.paymentValue != null)
                    .map(r => ({ value: Number(r.paymentValue), name: r.name || `${r.paymentValue}` }))
            );
            setDebts(Array.isArray(debtRaw) ? debtRaw : []);
            setForgotRows(Array.isArray(forgotRaw) ? forgotRaw : []);
            setAgentNameMap(names);
            if (imdSummary && typeof imdSummary === 'object') {
                setImdBagReturn({ qty: imdSummary.returnedQty || 0, amount: imdSummary.returnedAmount || 0 });
            } else {
                // อ่านยอดคืนจาก IMD ไม่ได้ ≠ ไม่มีการคืน — ใช้เฉพาะช่องที่บัญชีกรอก และบอกให้รู้
                setImdBagReturn(null);
                setLoadError(prev => prev || 'อ่านยอดคืนประกันกระสอบจาก IMD หน้าโรงไม่สำเร็จ — ช่อง "คืนเงินประกัน" ใช้เฉพาะที่บัญชีกรอก');
            }
            if (entryRaw && typeof entryRaw === 'object') {
                setEntry({
                    otherExpenses: entryRaw.otherExpenses || 0,
                    pettyCash: entryRaw.pettyCash || 0,
                    bagDepositReturn: entryRaw.bagDepositReturn || 0,
                });
                setEntryLoaded(true);
            } else {
                // อ่านช่องที่บัญชีกรอกไม่สำเร็จ → ไม่โชว์ 0 หลอก ๆ แล้วเปิดให้บันทึกทับของจริง
                setEntry(EMPTY_ENTRY);
                setLoadError(prev => prev || 'อ่านช่องที่บัญชีกรอก (อื่น ๆ/เงินสดย่อย/คืนประกัน) ไม่สำเร็จ — ยังไม่ให้บันทึก');
            }
            setEntryDirty(false);
            setSaveMsg('');
        } finally {
            setLoading(false);
        }
    }, [checkToken]);

    useEffect(() => {
        setSearchParams({ date });
        loadAll(date);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [date]);

    const handleEntryChange = (field, value) => {
        setEntry(prev => ({ ...prev, [field]: value === '' ? '' : (parseFloat(value) || 0) }));
        setEntryDirty(true);
        setSaveMsg('');
    };

    const saveEntry = async () => {
        const token = checkToken();
        if (!token) return;
        setSaving(true);
        setSaveMsg('');
        try {
            const body = {
                fuelCost: 0, // ค่าน้ำมันดึงจาก IMD แล้ว — คอลัมน์เดิมคงไว้ ไม่ใช้
                otherExpenses: Number(entry.otherExpenses) || 0,
                pettyCash: Number(entry.pettyCash) || 0,
                bagDepositReturn: Number(entry.bagDepositReturn) || 0,
            };
            const res = await fetch(`${API_BASE_URL}/saledata/api/module/accounting-daily?date=${date}`, {
                method: 'PUT',
                headers: { ...authHeaders(token), 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
            });
            if (res.ok) { setSaveMsg('บันทึกแล้ว ✅'); setEntryDirty(false); }
            else setSaveMsg('บันทึกไม่สำเร็จ ❌');
        } catch { setSaveMsg('เกิดข้อผิดพลาด ❌'); }
        finally { setSaving(false); }
    };

    const calc = useMemo(() => {
        const allReports = [...routeReports, ...agentReports];
        const items = [];
        allReports.forEach(r => (fullByCode[r.reportCode]?.itemsBreakdown?.items || []).forEach(it => items.push(it)));
        const products = buildProductRows(items, bagSales.rows);

        // ส่วนที่ 1 ต้องเท่ากับยอดใบรายงานทุกสาย (+ขายกระสอบเปล่า) — ไม่เท่าให้ขึ้นป้ายเตือน
        const reportQty = allReports.reduce((s, r) => s + (r.totalQty || 0), 0) + bagSales.qty;
        const reportAmount = round2(allReports.reduce((s, r) => s + (r.totalPrice || 0), 0) + bagSales.total);

        // เงินส่งของเอเย่นต์อื่น (บัญชีกรอกผ่าน AgentSaleDataDetail) ต้องเข้ายอดโอน/น้ำมัน/แยกแบงค์ด้วย — ยอดขายของเขาอยู่ในส่วนที่ 2 อยู่แล้ว
        const payParts = [...routeReports, ...agentReports].map(r => ({
            r,
            agg: aggregatePayments(fullByCode[r.reportCode]?.payments, denoms),
        }));
        const cash = mergeDenoms(payParts.map(p => p.agg), denoms);

        const systemCashKeys = new Set();
        const cashRoutes = groupByRoute(routeReports, r => {
            const paid = aggregatePayments(fullByCode[r.reportCode]?.payments, denoms).cashTotal;
            const res = resolveRouteCash(paid, { factory: (r.sellerId || '').toUpperCase() === FACTORY_AGENT_ID, round: roundByCode[r.reportCode] });
            if (res.source === 'system') systemCashKeys.add(r.sellerId || r.reportCode);
            return res.value;
        }).map(r => ({ ...r, system: systemCashKeys.has(r.key) }));
        const bagRoutes = groupByRoute(routeReports, r => r.totalQty || 0);
        const agentQty = agentReports.reduce((s, r) => s + (r.totalQty || 0), 0);
        const routeQty = bagRoutes.reduce((s, r) => s + r.value, 0);
        // เงินสดของเอเย่นต์อื่น (ไม่ใช่ AGC-TOKYO) — ยังไม่กรอกเงินส่ง = 0 ไม่ใช้ "ตามระบบ"
        const agentCash = round2(agentReports.reduce((s, r) => s + aggregatePayments(fullByCode[r.reportCode]?.payments, denoms).cashTotal, 0));
        const cashRoutesTotal = round2(cashRoutes.reduce((s, r) => s + r.value, 0) + agentCash);

        // ค้างชำระ = ยอดค้างของบิลวันนี้ · เก็บค้างจ่าย = ค้างที่จ่ายแล้ววันนี้ (ทะเบียนลูกค้าค้างจ่ายของ saledata)
        const debtNewList = debts.filter(x => x.billDate === date);
        const debtPaidList = debts.filter(x => x.status === 'PAID' && String(x.paidAt || '').slice(0, 10) === date);
        const debtNew = round2(debtNewList.reduce((s, x) => s + (x.amount || 0), 0));
        const debtPaid = round2(debtPaidList.reduce((s, x) => s + (x.amount || 0), 0));
        // ช่องทางที่บัญชีเลือกตอนชำระ: TRANSFER = เงินเข้าบัญชีไม่ใช่เงินสด · CASH/ไม่ระบุ (แถวเก่า, IMD) = เงินสด
        const debtPaidTransfer = round2(debtPaidList.filter(x => x.paidMethod === 'TRANSFER').reduce((s, x) => s + (x.amount || 0), 0));
        const debtPaidCash = round2(debtPaid - debtPaidTransfer);

        const summary = buildSummary({
            salesTotal: products.totalAmount,
            debtNew,
            debtPaid,
            debtPaidTransfer,
            bagDepositReturn: bagReturnEffective(entry.bagDepositReturn, imdBagReturn),
            transfer: cash.transfer,
            fuel: cash.fuel,
            other: entry.otherExpenses,
            pettyCash: entry.pettyCash,
        });
        const warnings = buildWarnings({
            // ยอดกระสอบทุกสายไม่รวมกระสอบเปล่า (ประกันถุง) ⇒ เทียบกับส่วนที่ 1 โดยตัดแถวประกันถุงออกด้วย
            salesQty: products.totalQty - bagSales.qty,
            bagsQty: routeQty + agentQty,
            salesAmount: products.totalAmount,
            routeAmount: reportAmount,
            cashByDenom: cash.cashTotal,
            cashByRoute: cashRoutesTotal,
            cashTotal: summary.cashTotal,
        });
        return { products, reportQty, reportAmount, cash, cashRoutes, agentCash, cashRoutesTotal, bagRoutes, agentQty, routeQty, debtNew, debtPaid, debtPaidTransfer, debtPaidCash, debtPaidList, summary, warnings };
    }, [routeReports, agentReports, fullByCode, roundByCode, bagSales, denoms, debts, date, entry, imdBagReturn]);

    const routeName = (r) => agentNameMap[(r.key || '').toUpperCase()] || r.name;
    const { products, cash, summary } = calc;
    // บิลค้างจากร้านที่บัญชีไม่อนุญาตให้ค้าง (NOT_ALLOWED_AT_RECEIPT) ของใบรายงาน/บิลวันที่เลือก — เตือนที่นี่ ไม่ใช่แค่ใน /debt-management
    const shownCodes = new Set([...routeReports, ...agentReports].map(r => r.reportCode));
    const notAllowedDebtCount = debts.filter(d =>
        d.status === 'OUTSTANDING' && d.flag === 'NOT_ALLOWED_AT_RECEIPT' && (shownCodes.has(d.reportCode) || d.billDate === date)
    ).length;
    const sellerNameMap = {};
    [...routeReports, ...agentReports].forEach(r => { if (r.sellerId) sellerNameMap[r.sellerId] = r.nameuser || r.username || r.sellerId; });
    const productNameMap = {};
    Object.values(fullByCode).forEach(f => (f?.itemsBreakdown?.items || []).forEach(it => { if (it.iceCode && it.productName) productNameMap[it.iceCode] = it.productName; }));
    const forgotTotalQty = forgotRows.reduce((s, r) => s + (r.quantity || 0), 0);
    const dateLabel = thaiDate(date);
    const noData = !loading && routeReports.length === 0 && agentReports.length === 0;

    const entryInput = (field, color) => (
        <input
            type="number" step="0.01" min="0"
            value={entry[field]}
            disabled={!entryLoaded}
            onChange={e => handleEntryChange(field, e.target.value)}
            style={color ? { ...inputStyle, color } : inputStyle}
        />
    );

    return (
        <div className="btc-container acct-print-root" style={{ maxWidth: '520px', margin: '0 auto' }}>
            <style>{PRINT_CSS}</style>
            <div className="acct-no-print" style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px', flexWrap: 'wrap', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <button className="btc-btn" style={{ background: '#6c757d', padding: '4px 10px', fontSize: '13px' }} onClick={() => navigate(-1)}>← กลับ</button>
                    <h1 style={{ margin: 0, fontSize: '18px' }}>รายงานบัญชีรายวัน</h1>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <label style={{ fontSize: '13px' }}>วันที่:</label>
                    <input type="date" value={date} onChange={e => e.target.value && setDate(e.target.value)} style={{ padding: '4px 8px', border: '1px solid #ced4da', borderRadius: '4px' }} />
                    <button className="btc-btn" style={{ background: '#E60012', padding: '4px 12px', fontSize: '13px' }} onClick={() => navigate('/debt-management')}>💰 จัดการยอดค้าง</button>
                    <button className="btc-btn" style={{ background: '#17a2b8', padding: '4px 12px', fontSize: '13px' }} onClick={() => window.print()}>🖨 พิมพ์</button>
                </div>
            </div>

            {loading && <p className="acct-no-print" style={{ color: '#6c757d', textAlign: 'center' }}>กำลังโหลดข้อมูล...</p>}
            {loadError && (
                <div role="alert" className="acct-no-print" style={{ background: '#f8d7da', border: '2px solid #c62828', color: '#842029', borderRadius: '6px', padding: '8px 12px', marginBottom: '12px', fontSize: '13px', fontWeight: 700 }}>
                    ⚠ {loadError}
                </div>
            )}
            {noData && !loadError && <p className="acct-no-print" style={{ color: '#adb5bd', textAlign: 'center' }}>ไม่มีรายงานการขายของวันที่เลือก</p>}

            {notAllowedDebtCount > 0 && !loading && (
                <div
                    role="alert" data-testid="acct-notallowed-debt" className="acct-no-print"
                    onClick={() => navigate('/debt-management')}
                    style={{ cursor: 'pointer', background: '#fff3cd', border: '2px solid #e8590c', color: '#b45309', borderRadius: '6px', padding: '8px 12px', marginBottom: '14px', fontSize: '13px', fontWeight: 700 }}
                >
                    ⚠ มี {notAllowedDebtCount} บิลค้างจากร้านที่ไม่ได้อนุญาตให้ค้าง — ตรวจที่จัดการยอดค้าง
                </div>
            )}

            {calc.warnings.length > 0 && !loading && (
                <div data-testid="acct-warnings" style={{ background: '#fff3cd', border: '2px solid #e8590c', color: '#b45309', borderRadius: '6px', padding: '8px 12px', marginBottom: '14px', fontSize: '13px' }}>
                    <div style={{ fontWeight: 700 }}>⚠ ตัวเลขที่ควรเท่ากันไม่เท่ากัน (ระบบไม่ปรับให้ — ตรวจที่มา)</div>
                    {calc.warnings.map(w => (
                        <div key={w.label} data-testid="acct-warning">• {w.label} — ส่วนต่าง {w.unit === 'บาท' ? fmt(w.diff) : fmtInt(w.diff)} {w.unit}</div>
                    ))}
                </div>
            )}

            {/* ส่วนที่ 1 */}
            <table style={tableStyle} data-testid="acct-part1">
                <thead>
                    <tr>
                        <th style={th} colSpan="3">ส่วนที่ 1 - สรุปยอดขายสินค้า</th>
                        <th style={th}>{dateLabel}</th>
                    </tr>
                    <tr>
                        <th style={th}>ประเภทสินค้า</th>
                        <th style={th}>ราคา/หน่วย (บาท)</th>
                        <th style={th}>จำนวน (กระสอบ)</th>
                        <th style={th}>จำนวนเงิน (บาท)</th>
                    </tr>
                </thead>
                <tbody>
                    {/* "ขายกระสอบ" ในระบบ = เงินประกันกระสอบ — ไม่มียอดในวันนั้นก็โชว์ 0 ตามแผ่น */}
                    {!products.rows.some(r => r.name === BAG_DEPOSIT_NAME) && (
                        <tr>
                            <td style={cell}>{BAG_DEPOSIT_NAME}</td>
                            <td style={{ ...cell, ...right }}>-</td>
                            <td style={{ ...cell, ...right }}>0</td>
                            <td style={{ ...cell, ...right }}>{fmt(0)}</td>
                        </tr>
                    )}
                    {products.rows.map((r, i) => (
                        <tr key={i}>
                            <td style={cell}>{r.name}</td>
                            <td style={{ ...cell, ...right }}>{fmt(r.unitPrice)}</td>
                            <td style={{ ...cell, ...right }}>{fmtInt(r.qty)}</td>
                            <td style={{ ...cell, ...right }}>{fmt(r.total)}</td>
                        </tr>
                    ))}
                </tbody>
                <tfoot>
                    <tr>
                        <td style={{ ...total, textAlign: 'center' }} colSpan="2">รวม</td>
                        <td style={{ ...total, ...right }}>{fmtInt(products.totalQty)}</td>
                        <td style={{ ...total, ...right }}>{fmt(products.totalAmount)}</td>
                    </tr>
                </tfoot>
            </table>

            {/* ส่วนที่ 2 */}
            <table style={tableStyle} data-testid="acct-part2">
                <thead>
                    <tr>
                        <th style={th} colSpan="2">ส่วนที่ 2 - สรุปรายรับ - รายจ่าย</th>
                        <th style={th}>{dateLabel}</th>
                    </tr>
                    <tr>
                        <th style={th}>รายการ</th>
                        <th style={th}>จำนวนเงิน (บาท)</th>
                        <th style={th}>หมายเหตุ</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td style={{ ...cell, fontWeight: 700 }}>รวมเงินขายทั้งหมด</td>
                        <td style={{ ...cell, ...right, fontWeight: 700 }}>{fmt(products.totalAmount)}</td>
                        <td style={cell}></td>
                    </tr>
                    <tr>
                        <td style={cell}><b>หัก</b> : ยอดค้างชำระ</td>
                        <td style={{ ...cell, ...right, color: '#c00000' }}>{calc.debtNew ? `(${fmt(calc.debtNew)})` : fmt(0)}</td>
                        <td style={cell}></td>
                    </tr>
                    <tr>
                        <td style={cell}><b>เก็บ</b> : ยอดค้างจ่าย</td>
                        <td style={{ ...cell, ...right }}>{fmt(calc.debtPaid)}</td>
                        <td style={{ ...cell, fontSize: '12px' }}>
                            {calc.debtPaidList.map((x, i) => (
                                <span key={x.id}>
                                    {i > 0 && ', '}
                                    <CustomerLink name={x.customerName} code={x.customerId} />
                                </span>
                            ))}
                        </td>
                    </tr>
                    {calc.debtPaidTransfer > 0 && (
                        <tr data-testid="debt-paid-transfer-row">
                            <td style={cell}><b>หัก</b> : ค้างจ่ายที่รับเป็นโอน <span style={{ fontSize: '11px', color: '#6c757d' }}>(เงินสดค้างจ่าย {fmt(calc.debtPaidCash)} + โอน {fmt(calc.debtPaidTransfer)})</span></td>
                            <td style={{ ...cell, ...right, color: '#c00000' }}>({fmt(calc.debtPaidTransfer)})</td>
                            <td style={{ ...cell, fontSize: '12px' }}>
                                {calc.debtPaidList.filter(x => x.paidMethod === 'TRANSFER').map((x, i) => (
                                    <span key={x.id}>{i > 0 && ', '}{x.customerName || '-'}{x.paidNote ? ` (${x.paidNote})` : ''}</span>
                                ))}
                            </td>
                        </tr>
                    )}
                    <tr>
                        <td style={cell}><b>คืน</b> : เงินประกันค่ากระสอบ</td>
                        <td data-testid="bag-return-effective" style={{ ...cell, ...right, color: '#c00000' }}>({fmt(bagReturnEffective(entry.bagDepositReturn, imdBagReturn))})</td>
                        <td style={{ ...cell, fontSize: '11px', color: '#6c757d' }}>
                            <div>{bagReturnSourceNote(entry.bagDepositReturn, imdBagReturn)}</div>
                            <div className="acct-no-print" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>กรอกเอง (ทับ): {entryInput('bagDepositReturn', '#c00000')}</div>
                        </td>
                    </tr>
                    <tr>
                        <td style={cell}><b>หัก</b> : ยอดโอน</td>
                        <td style={{ ...cell, ...right, color: '#c00000' }}>{cash.transfer ? `(${fmt(cash.transfer)})` : fmt(0)}</td>
                        <td style={{ ...cell, fontSize: '11px', color: '#6c757d' }}>จากแอพส่งเงินของคนขับ</td>
                    </tr>
                    <tr>
                        <td style={cell}><b>หัก</b> : ค่าน้ำมัน</td>
                        <td style={{ ...cell, ...right, color: '#c00000' }}>{cash.fuel ? `(${fmt(cash.fuel)})` : fmt(0)}</td>
                        <td style={{ ...cell, fontSize: '11px', color: '#6c757d' }}>จากแอพส่งเงินของคนขับ</td>
                    </tr>
                    <tr>
                        <td style={cell}><b>หัก</b> : ค่าใช้จ่ายอื่น ๆ</td>
                        <td style={{ ...cell, ...right }}>{entryInput('otherExpenses')}</td>
                        <td style={cell}></td>
                    </tr>
                    <tr>
                        <td style={{ ...total }}>รวมเงินสดทั้งหมด</td>
                        <td style={{ ...total, ...right }} data-testid="acct-cash-total">{fmt(summary.cashTotal)}</td>
                        <td style={total}></td>
                    </tr>
                    <tr>
                        <td style={{ ...cell, color: '#0033cc', fontWeight: 700 }}><b>หัก</b> : เก็บไว้เป็นเงินสดย่อย</td>
                        <td style={{ ...cell, ...right }}>{entryInput('pettyCash')}</td>
                        <td style={cell}></td>
                    </tr>
                    <tr>
                        <td style={{ ...cell, background: YELLOW, fontWeight: 700 }}>รวมเงินนำฝากธนาคารกรุงไทย</td>
                        <td style={{ ...cell, ...right, background: YELLOW, fontWeight: 700 }} data-testid="acct-bank-deposit">{fmt(summary.bankDeposit)}</td>
                        <td style={{ ...cell, background: YELLOW }}></td>
                    </tr>
                </tbody>
            </table>
            <div className="acct-no-print" style={{ margin: '-8px 0 16px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                <button className="btc-btn" style={{ background: entryDirty ? '#E60012' : '#6c757d', padding: '6px 20px', fontWeight: 700 }} onClick={saveEntry} disabled={saving || !entryLoaded}>
                    {saving ? 'กำลังบันทึก...' : 'บันทึก'}
                </button>
                {saveMsg && <span style={{ fontSize: '13px', color: saveMsg.includes('✅') ? '#2e7d32' : '#c62828' }}>{saveMsg}</span>}
                {entryDirty && !saveMsg && <span style={{ fontSize: '12px', color: '#856404' }}>⚠ มีการแก้ไขที่ยังไม่ได้บันทึก</span>}
                <span style={{ fontSize: '11px', color: '#adb5bd' }}>ช่องที่กรอกได้: คืนประกัน / น้ำมัน / อื่น ๆ / เงินสดย่อย</span>
            </div>

            {/* ส่วนที่ 3 */}
            <table style={tableStyle} data-testid="acct-part3">
                <thead>
                    <tr>
                        <th style={th} colSpan="2">ยอดเงินสดทั้งหมด</th>
                        <th style={th}>{dateLabel}</th>
                    </tr>
                    <tr>
                        <th style={th}>ประเภทเงิน</th>
                        <th style={th}>จำนวน (ใบ)</th>
                        <th style={th}>จำนวนเงิน (บาท)</th>
                    </tr>
                </thead>
                <tbody>
                    {cash.denomList.map(d => (
                        <tr key={d.value}>
                            <td style={{ ...cell, textAlign: 'center' }}>{d.value === cash.denomList[0].value ? 'เหรียญ' : fmtInt(d.value)}</td>
                            <td style={{ ...cell, ...right }}>{fmtInt(cash.byDenom[d.value].count)}</td>
                            <td style={{ ...cell, ...right }}>{fmt(cash.byDenom[d.value].total)}</td>
                        </tr>
                    ))}
                </tbody>
                <tfoot>
                    <tr>
                        <td style={{ ...total, textAlign: 'center' }} colSpan="2">รวม</td>
                        <td style={{ ...total, ...right }}>{fmt(cash.cashTotal)}</td>
                    </tr>
                </tfoot>
            </table>

            {/* ส่วนที่ 4 */}
            <table style={tableStyle} data-testid="acct-part4">
                <thead>
                    <tr>
                        <th style={th} colSpan="2">ยอดเงินสดทุกสาย</th>
                        <th style={th}>{dateLabel}</th>
                    </tr>
                    <tr>
                        <th style={{ ...th, width: '60px' }}>สายที่</th>
                        <th style={th}>สาย</th>
                        <th style={th}>จำนวนเงิน (บาท)</th>
                    </tr>
                </thead>
                <tbody>
                    {calc.cashRoutes.map((r, i) => (
                        <tr key={r.key}>
                            <td style={{ ...cell, textAlign: 'center' }}>{i + 1}</td>
                            <td style={cell}>
                                {routeName(r)}
                                {r.system && <div data-testid="acct-cash-system-note" style={{ fontSize: '11px', color: '#6c757d' }}>ตามระบบ (ยังไม่มีการกรอกเงินส่ง)</div>}
                            </td>
                            <td style={{ ...cell, ...right }}>{fmt(r.value)}</td>
                        </tr>
                    ))}
                    {agentReports.length > 0 && (
                        <tr data-testid="acct-cash-agent-row">
                            <td style={{ ...cell, textAlign: 'center' }}>{calc.cashRoutes.length + 1}</td>
                            <td style={cell}>เอเย่นต์</td>
                            <td style={{ ...cell, ...right }}>{fmt(calc.agentCash)}</td>
                        </tr>
                    )}
                </tbody>
                <tfoot>
                    <tr>
                        <td style={{ ...total, textAlign: 'center' }} colSpan="2">รวม</td>
                        <td style={{ ...total, ...right }}>{fmt(calc.cashRoutesTotal)}</td>
                    </tr>
                </tfoot>
            </table>

            {/* ส่วนที่ 5 */}
            <table style={tableStyle} data-testid="acct-part5">
                <thead>
                    <tr>
                        <th style={th} colSpan="2">ยอดกระสอบทุกสาย</th>
                        <th style={th}>{dateLabel}</th>
                    </tr>
                    <tr>
                        <th style={{ ...th, width: '60px' }}>สายที่</th>
                        <th style={th}>ชื่อสาย</th>
                        <th style={th}>จำนวน (กระสอบ)</th>
                    </tr>
                </thead>
                <tbody>
                    {calc.bagRoutes.map((r, i) => (
                        <tr key={r.key}>
                            <td style={{ ...cell, textAlign: 'center' }}>{i + 1}</td>
                            <td style={cell}>{routeName(r)}</td>
                            <td style={{ ...cell, ...right }}>{fmtInt(r.value)}</td>
                        </tr>
                    ))}
                    <tr>
                        <td style={{ ...cell, textAlign: 'center' }}>{calc.bagRoutes.length + 1}</td>
                        <td style={cell}>เอเย่นต์</td>
                        <td style={{ ...cell, ...right }}>{fmtInt(calc.agentQty)}</td>
                    </tr>
                </tbody>
                <tfoot>
                    <tr>
                        <td style={{ ...total, textAlign: 'center' }} colSpan="2">รวม</td>
                        <td style={{ ...total, ...right }} data-testid="acct-bags-total">{fmtInt(calc.routeQty + calc.agentQty)}</td>
                    </tr>
                    <tr>
                        <td style={{ ...total, textAlign: 'center', background: PINK_DARK }} colSpan="2">ไม่รวมเอเย่นต์</td>
                        <td style={{ ...total, ...right, background: PINK_DARK }} data-testid="acct-bags-excl-agent">{fmtInt(calc.routeQty)}</td>
                    </tr>
                </tfoot>
            </table>

            {/* รายงานลืมส่ง — จำนวนที่ไม่มีลูกค้ากำกับ (customerId=FORGOT) แยกตามสาย × สินค้า · ข้อมูลประกอบ รวมอยู่ในยอดสายด้านบนแล้ว ไม่ต้องบวกเพิ่ม · ไม่พิมพ์ลงแผ่นบัญชี */}
            <table style={tableStyle} data-testid="acct-forgot" className="acct-no-print">
                <thead>
                    <tr><th style={th} colSpan="4">รายงานลืมส่ง {dateLabel}</th></tr>
                    <tr>
                        <th style={th}>สาย</th><th style={th}>สินค้า</th><th style={th}>จำนวน</th><th style={th}>จำนวนรายการ</th>
                    </tr>
                </thead>
                <tbody>
                    {forgotRows.map((r, i) => (
                        <tr key={i}>
                            <td style={cell}>{sellerNameMap[r.sellerId] || r.sellerId}</td>
                            <td style={cell}>{productNameMap[r.iceCode] || r.iceCode}</td>
                            <td style={{ ...cell, ...right }}>{fmtInt(r.quantity)}</td>
                            <td style={{ ...cell, ...right }}>{fmtInt(r.count)}</td>
                        </tr>
                    ))}
                    {forgotRows.length === 0 && !loading && (
                        <tr><td colSpan="4" style={{ ...cell, textAlign: 'center', color: '#adb5bd' }}>ไม่มีรายการลืมส่งวันนี้</td></tr>
                    )}
                </tbody>
                {forgotRows.length > 0 && (
                    <tfoot>
                        <tr>
                            <td style={total} colSpan="2">รวม</td>
                            <td style={{ ...total, ...right }} data-testid="acct-forgot-total">{fmtInt(forgotTotalQty)}</td>
                            <td style={total}>-</td>
                        </tr>
                    </tfoot>
                )}
            </table>
        </div>
    );
};

export default AccountingDailyReport;
